import { Schema } from 'prosemirror-model'
import { schema as basicSchema } from 'prosemirror-schema-basic'
import { addListNodes } from 'prosemirror-schema-list'
import { tableNodes } from 'prosemirror-tables'
import {
  kLegalCitationDisplayNameAttr,
  kLegalCitationIdAttr,
  kLegalCitationNotLinkedAttr,
  kLegalCitationUrlAttr,
  kReferenceMarkerDisplayNameAttr,
  kReferenceMarkerIdAttr,
  kReferenceMarkerLabelAttr,
  kReferenceMarkerUrlAttr,
  isReferenceViewerHref,
} from '../utils/citation-helpers'

/**
 * Parsed span attributes are untrusted (paste round-trips through parseDOM): only
 * reference-viewer URLs may enter the document; anything else degrades to no-URL.
 */
function sanitizeViewerUrl(url: string | null): string {
  return url && isReferenceViewerHref(url) ? url : ''
}

/**
 * ProseMirror schema for the artifact editor.
 * Extends the basic schema with lists, tables, and inline citation reference markers.
 */
export const markdownEditorSchema = new Schema({
  nodes: addListNodes(basicSchema.spec.nodes, 'paragraph block*', 'block')
    .append(
      tableNodes({
        tableGroup: 'block',
        cellContent: 'block+',
        cellAttributes: {
          background: {
            default: null,
            getFromDOM(dom) {
              return (dom as HTMLElement).style.backgroundColor || null
            },
            setDOMAttr(value, attrs) {
              if (value) attrs.style = (attrs.style || '') + `background-color: ${value};`
            },
          },
        },
      })
    )
    // Inline citation badges — [ref_xxx] markers preprocessed into <span> elements
    // are parsed here as referenceMarker nodes, rendered by ReferenceMarkerView.
    // Uses content:'text*' instead of atom:true to avoid ProseMirror injecting
    // separator/trailing-break elements that cause visible gaps after badges.
    // The NodeView enforces contentEditable=false to prevent editing.
    .addToEnd('referenceMarker', {
      group: 'inline',
      inline: true,
      content: 'text*',
      selectable: false,
      attrs: {
        citationId: { default: '' },
        label: { default: '' },
        viewerUrl: { default: '' },
        displayName: { default: '' },
      },
      parseDOM: [
        {
          tag: `span[${kReferenceMarkerIdAttr}]`,
          getAttrs(dom) {
            const el = dom as HTMLElement
            return {
              citationId: el.getAttribute(kReferenceMarkerIdAttr) || '',
              label: el.getAttribute(kReferenceMarkerLabelAttr) || '',
              viewerUrl: sanitizeViewerUrl(el.getAttribute(kReferenceMarkerUrlAttr)),
              displayName: el.getAttribute(kReferenceMarkerDisplayNameAttr) || '',
            }
          },
        },
      ],
      toDOM(node) {
        return [
          'span',
          {
            [kReferenceMarkerIdAttr]: node.attrs.citationId,
            [kReferenceMarkerLabelAttr]: node.attrs.label,
            [kReferenceMarkerUrlAttr]: node.attrs.viewerUrl,
            [kReferenceMarkerDisplayNameAttr]: node.attrs.displayName,
            class: 'reference-marker-badge',
          },
          `[${node.attrs.citationId}]`,
        ]
      },
    }),
  // Legal citation links — [authority text](ref_xxx) preprocessed into <span> elements
  // are parsed here as a mark on the authority text, so the text stays editable and the
  // citation identity survives edits. Added BEFORE the basic marks so it sorts outermost:
  // serialization must produce one citation span wrapping <em> segments (not spans
  // fragmented per formatting run), or the turndown round-trip would split the link.
  marks: basicSchema.spec.marks.addBefore('link', 'legalCitation', {
    attrs: {
      citationId: { default: '' },
      viewerUrl: { default: '' },
      displayName: { default: '' },
      // The citation id isn't resolvable in this artifact version (e.g. carried
      // across revisions) — rendered greyed out, "Not linked" tooltip.
      notLinked: { default: false },
    },
    // Typing at the edge of a citation shouldn't extend it
    inclusive: false,
    parseDOM: [
      {
        tag: `span[${kLegalCitationIdAttr}]`,
        getAttrs(dom) {
          const el = dom as HTMLElement
          return {
            citationId: el.getAttribute(kLegalCitationIdAttr) || '',
            viewerUrl: sanitizeViewerUrl(el.getAttribute(kLegalCitationUrlAttr)),
            displayName: el.getAttribute(kLegalCitationDisplayNameAttr) || '',
            notLinked: el.getAttribute(kLegalCitationNotLinkedAttr) === 'true',
          }
        },
      },
    ],
    toDOM(mark) {
      return [
        'span',
        {
          [kLegalCitationIdAttr]: mark.attrs.citationId,
          [kLegalCitationUrlAttr]: mark.attrs.viewerUrl,
          [kLegalCitationDisplayNameAttr]: mark.attrs.displayName,
          ...(mark.attrs.notLinked ? { [kLegalCitationNotLinkedAttr]: 'true' } : {}),
          title: mark.attrs.displayName || undefined,
          // Keep these treatments in sync with LegalCitationLink.tsx — the same citation
          // state must look identical in chat and in the canvas.
          class: mark.attrs.notLinked
            ? 'legal-citation-link text-brand-neutral-400 underline decoration-brand-neutral-400/40 underline-offset-2'
            : mark.attrs.viewerUrl
            ? 'legal-citation-link text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-800 hover:decoration-brand-800 cursor-pointer'
            : 'legal-citation-link',
        },
        0,
      ]
    },
  }),
})
